import { useQuery } from '@tanstack/react-query';
import { useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTRPC } from '../trpc.js';
import { Select } from '../ui/Select.js';

/** What a list is called, here and in every other place one is named. */
export const LIST_NAME_PLACEHOLDER = 'List name...';

export interface NewListModalProps {
  /** True while the list is being made, so a second click cannot make two. */
  pending: boolean;
  onCreate: (list: { name: string; portfolioCompanyId: string | null }) => void;
  onCancel: () => void;
}

/**
 * Naming a new list, and optionally who it is for. The same dialog shape as
 * `ConfirmModal`, with the one field the question needs.
 */
export function NewListModal(props: NewListModalProps): React.JSX.Element {
  const titleId = useId();
  const trpc = useTRPC();
  const companies = useQuery(trpc.portfolio.list.queryOptions());
  const [name, setName] = useState('');
  const [companyId, setCompanyId] = useState('');
  const { onCancel } = props;

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      event.preventDefault();
      onCancel();
    }
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [onCancel]);

  const canCreate = name.trim() !== '' && !props.pending;

  return createPortal(
    <div
      className="modal-backdrop"
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) onCancel();
      }}
    >
      <form
        className="modal add-to-list"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onSubmit={(event) => {
          event.preventDefault();
          if (!canCreate) return;
          props.onCreate({
            name: name.trim(),
            portfolioCompanyId: companyId === '' ? null : companyId,
          });
        }}
      >
        <h2 id={titleId} className="modal-title">
          New list
        </h2>
        <label className="field">
          <span className="subtle">Name</span>
          <input
            autoFocus
            value={name}
            placeholder={LIST_NAME_PLACEHOLDER}
            onChange={(event) => setName(event.target.value)}
          />
        </label>
        <label className="field">
          <span className="subtle">For</span>
          <Select
            label="For which portfolio company"
            value={companyId}
            onChange={setCompanyId}
            options={[
              { value: '', label: 'For no company in particular' },
              ...(companies.data ?? []).map((company) => ({
                value: company.id,
                label: `For ${company.name}`,
              })),
            ]}
          />
        </label>
        <div className="modal-actions">
          <button className="button" type="button" onClick={onCancel}>
            Cancel
          </button>
          <button className="button primary" type="submit" disabled={!canCreate}>
            Create
          </button>
        </div>
      </form>
    </div>,
    document.body,
  );
}
